// # Financial Health Component for Net Revenue Dashboard
import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  ComposedChart
} from 'recharts';
import { 
  FaChartLine, 
  FaMoneyBillWave,
  FaPercentage,
  FaArrowUp,
  FaArrowDown,
  FaMinus,
  FaWallet,
  FaChartArea,
  FaFireAlt,
  FaQuestionCircle,
  FaTimes
} from 'react-icons/fa';
import './financial_health.css';
// # Import the SignificanceMsg component
import { getSignificanceMessage } from '../../financial_utils/significance_msg';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../../../../firebase/config';

// # Add a simple SignificanceMsg component to replace the imported one
const SignificanceMsg = ({ metricType }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  const toggleTooltip = () => {
    setShowTooltip(!showTooltip);
  };
  
  const message = getSignificanceMessage(metricType);
  
  return (
    <span className="fncl_hlth_significance">
      <FaQuestionCircle onClick={toggleTooltip} />
      {showTooltip && (
        <div className="fncl_hlth_tooltip">
          <div className="fncl_hlth_tooltip-header">
            <h4>{message.title}</h4>
            <FaTimes onClick={toggleTooltip} />
          </div>
          <p>{message.description}</p>
        </div>
      )}
    </span>
  );
};

// # Add default data structure
const defaultFinancialData = {
  netProfitMargin: [],
  burnRate: [],
  monthlyRevenue: [],
  monthlyExpenses: [],
  monthlySalaryDeductions: []
};

const FinancialHealth = ({ financialData = defaultFinancialData }) => {
  // # State for time period filtering
  const [timePeriod, setTimePeriod] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filteredData, setFilteredData] = useState({
    netProfitMargin: [],
    burnRate: [],
    monthlyRevenue: [],
    monthlyExpenses: [],
    monthlySalaryDeductions: []
  });
  
  // # Calculate financial health metrics
  const [healthMetrics, setHealthMetrics] = useState({
    averageProfitMargin: 0,
    averageBurnRate: 0,
    cashRunway: 0,
    revenueGrowth: 0,
    cashFlowPositive: false,
    lastMonthRevenue: 0,
    lastMonthExpenses: 0,
    lastMonthProfit: 0,
    expenseBreakdown: {}
  });
  
  // # Filter data when time period changes
  useEffect(() => {
    if (!financialData) return;
    
    // # Get current date for filtering
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    
    // # Filter data based on selected time period
    let filteredNetProfit = [...financialData.netProfitMargin || []];
    let filteredBurnRate = [...financialData.burnRate || []];
    let filteredMonthlyRevenue = [...financialData.monthlyRevenue || []];
    let filteredMonthlyExpenses = [...financialData.monthlyExpenses || []];
    let filteredSalaryDeductions = [...financialData.monthlySalaryDeductions || []];
    
    if (timePeriod === '3months') {
      // # Filter last 3 months
      const threeMonthsAgo = new Date(currentYear, currentMonth - 3, 1);
      
      const filterByDate = (item) => {
        if (!item || !item.month) return false;
        const [year, month] = item.month.split('-').map(Number);
        const itemDate = new Date(year, month - 1, 1);
        return itemDate >= threeMonthsAgo;
      };
      
      filteredNetProfit = filteredNetProfit.filter(filterByDate);
      filteredBurnRate = filteredBurnRate.filter(filterByDate);
      filteredMonthlyRevenue = filteredMonthlyRevenue.filter(filterByDate);
      filteredMonthlyExpenses = filteredMonthlyExpenses.filter(filterByDate);
      filteredSalaryDeductions = filteredSalaryDeductions.filter(filterByDate);
      
    } else if (timePeriod === '6months') {
      // # Filter last 6 months
      const sixMonthsAgo = new Date(currentYear, currentMonth - 6, 1);
      
      const filterByDate = (item) => {
        if (!item || !item.month) return false;
        const [year, month] = item.month.split('-').map(Number);
        const itemDate = new Date(year, month - 1, 1);
        return itemDate >= sixMonthsAgo;
      };
      
      filteredNetProfit = filteredNetProfit.filter(filterByDate);
      filteredBurnRate = filteredBurnRate.filter(filterByDate);
      filteredMonthlyRevenue = filteredMonthlyRevenue.filter(filterByDate);
      filteredMonthlyExpenses = filteredMonthlyExpenses.filter(filterByDate);
      filteredSalaryDeductions = filteredSalaryDeductions.filter(filterByDate);
      
    } else if (timePeriod === '1year') {
      // # Filter last year
      const oneYearAgo = new Date(currentYear - 1, currentMonth, 1);
      
      const filterByDate = (item) => {
        if (!item || !item.month) return false;
        const [year, month] = item.month.split('-').map(Number);
        const itemDate = new Date(year, month - 1, 1);
        return itemDate >= oneYearAgo;
      };
      
      filteredNetProfit = filteredNetProfit.filter(filterByDate);
      filteredBurnRate = filteredBurnRate.filter(filterByDate);
      filteredMonthlyRevenue = filteredMonthlyRevenue.filter(filterByDate);
      filteredMonthlyExpenses = filteredMonthlyExpenses.filter(filterByDate);
      filteredSalaryDeductions = filteredSalaryDeductions.filter(filterByDate);
    }
    
    // # Update filtered data
    setFilteredData({
      netProfitMargin: filteredNetProfit,
      burnRate: filteredBurnRate,
      monthlyRevenue: filteredMonthlyRevenue,
      monthlyExpenses: filteredMonthlyExpenses,
      monthlySalaryDeductions: filteredSalaryDeductions
    });
    
    // # Calculate health metrics
    if (filteredNetProfit.length > 0) {
      // # Average profit margin
      const avgMargin = filteredNetProfit.reduce((sum, item) => sum + (item.margin || 0), 0) / filteredNetProfit.length;
      
      // # Average burn rate calculation - CORRECTED: (Total Expenses / Available Cash) * 100
      // # NOTE: Expenses only, not including salaries
      const avgBurnRate = filteredBurnRate.reduce((sum, item) => {
        const expenses = item.expenses?.total || 0; // Only expenses, not salaries
        const availableCash = item.availableCash || 0;
        // # If available cash is 0, avoid division by zero
        return sum + (availableCash > 0 ? (expenses / availableCash) * 100 : 0);
      }, 0) / filteredBurnRate.length;
      
      // # Last month's data
      const sortedByMonth = [...filteredNetProfit].sort((a, b) => a.month.localeCompare(b.month));
      const lastMonthData = sortedByMonth[sortedByMonth.length - 1] || {};
      
      // # Revenue growth (comparing first and last month)
      const firstMonthRevenue = sortedByMonth[0]?.revenue || 0;
      const lastMonthRevenue = lastMonthData?.revenue || 0;
      const revenueGrowth = firstMonthRevenue > 0 
        ? ((lastMonthRevenue - firstMonthRevenue) / firstMonthRevenue) * 100 
        : 0;
      
      // # CORRECTED: Monthly burn rate calculation (Total Expenses / Available Cash) * 100
      // # NOTE: Expenses only, not including salaries
      const lastMonthExpenses = lastMonthData?.expenses?.total || 0; // Only expenses, not salaries
      const availableCash = lastMonthData?.availableCash || 0;
      const lastMonthBurnRate = availableCash > 0 ? (lastMonthExpenses / availableCash) * 100 : 0;

      // # CORRECTED: Calculate cash runway in months (Available Cash / Total Expenses)
      // # NOTE: Expenses only, not including salaries
      // # If monthly expenses are zero, set runway to a high number (effectively infinite)
      const cashRunway = lastMonthExpenses > 0 
        ? availableCash / lastMonthExpenses 
        : 999;
      
      // # Cash flow positive?
      const cashFlowPositive = lastMonthRevenue > (lastMonthData.salaries || 0);
      
      // # CORRECTED: Last Month Profit should be Net Revenue without considering salaries
      const lastMonthProfit = lastMonthData.netRevenue || 0;
      
      // # Update health metrics
      setHealthMetrics({
        averageProfitMargin: avgMargin,
        // # CORRECTED: Average burn rate is now (Total Expenses / Available Cash) * 100
        averageBurnRate: avgBurnRate,
        cashRunway: cashRunway,
        revenueGrowth: revenueGrowth,
        cashFlowPositive: cashFlowPositive,
        lastMonthRevenue: lastMonthRevenue,
        lastMonthExpenses: lastMonthExpenses,
        // # CORRECTED: Last Month Profit is Net Revenue (not considering salaries)
        lastMonthProfit: lastMonthProfit,
        expenseBreakdown: lastMonthData.expenses?.byCategory || {}
      });
    }
  }, [financialData, timePeriod]);
  
  // # Handle period change
  const handlePeriodChange = (period) => {
    setTimePeriod(period);
  };
  
  // # Format currency values
  const formatCurrency = (value) => {
    return `E£ ${Number(value).toLocaleString()}`;
  };
  
  // # Get trend indicator component
  const getTrendIndicator = (value) => {
    if (value > 0) {
      return <FaArrowUp className="fncl_hlth_trend-up" />;
    } else if (value < 0) {
      return <FaArrowDown className="fncl_hlth_trend-down" />;
    } else {
      return <FaMinus className="fncl_hlth_trend-neutral" />;
    }
  };

  // # Process monthly metrics with new data structure
  const processMonthlyMetrics = (monthlyData) => {
    const formattedData = Object.entries(monthlyData)
      .map(([month, data]) => {
        // # Calculate available cash (Net Revenue - Salary Deductions)
        const availableCash = data.revenue.net - data.salaries;

        // # Calculate net profit margin: (Net Revenue / Total Revenue) * 100
        const netProfitMargin = data.revenue.gross > 0
          ? (data.revenue.net / data.revenue.gross) * 100
          : 0;

        // # Calculate burn rate: (Total Expenses / Total Revenue) × 100
        const burnRatePercentage = data.revenue.gross > 0 
          ? (data.expenses.total / data.revenue.gross) * 100 
          : 0;

        return {
          month,
          revenue: data.revenue.gross,
          netRevenue: data.revenue.net,
          budgetAllocation: data.revenue.budget,
          expenses: data.expenses,
          salaries: data.salaries,
          availableCash,
          netProfitMargin,
          burnRatePercentage
        };
      })
      .sort((a, b) => a.month.localeCompare(b.month));

    return formattedData;
  };

  // # Fetch data on component mount
  useEffect(() => {
    const fetchAnalyticsData = async () => {
      setLoading(true);
      try {
        // # Get current year and month
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const startYear = currentYear - 1; // Get data for last year too

        const monthlyData = {};

        // # Fetch financial data for each year
        for (let year = startYear; year <= currentYear; year++) {
          const financialRef = collection(db, 'financial');
          const yearQuery = query(financialRef, 
            where('__name__', '>=', `${year}_01`),
            where('__name__', '<=', `${year}_12`)
          );
          const querySnapshot = await getDocs(yearQuery);

          // # Process each month's data
          for (const doc of querySnapshot.docs) {
            const monthData = doc.data();
            const monthKey = doc.id; // Format: "YYYY_MM"

            // # Initialize month data structure
            if (!monthlyData[monthKey]) {
              monthlyData[monthKey] = {
                revenue: {
                  gross: 0,
                  net: 0,
                  budget: 0
                },
                expenses: {
                  total: 0,
                  byCategory: {}
                },
                salaries: 0
              };
            }

            // # Process payments
            if (monthData.payments) {
              Object.values(monthData.payments).forEach(payment => {
                if (payment.transaction_type === 'payment') {
                  monthlyData[monthKey].revenue.gross += Number(payment.pay?.egp_total || 0);
                  monthlyData[monthKey].revenue.net += Number(payment.pay?.egp_net || 0);
                  monthlyData[monthKey].revenue.budget += Number(payment.budget_dvd?.egp_amount || 0);
                }
              });
            }

            // # Process expenses
            if (monthData.expenses) {
              Object.values(monthData.expenses).forEach(expense => {
                if (expense.transaction_type === 'expense') {
                  const amount = Number(expense.egp_total || 0);
                  monthlyData[monthKey].expenses.total += amount;
                  
                  // # Track by category
                  const category = expense.category || 'other';
                  monthlyData[monthKey].expenses.byCategory[category] = 
                    (monthlyData[monthKey].expenses.byCategory[category] || 0) + amount;
                }
              });
            }

            // # Process salaries
            if (monthData.salaries) {
              Object.values(monthData.salaries).forEach(salary => {
                monthlyData[monthKey].salaries += Number(salary.amount_egp || 0);
              });
            }
          }
        }

        // # Process the monthly data into metrics
        const processedData = processMonthlyMetrics(monthlyData);
        
        // # Update filtered data
        setFilteredData({
          netProfitMargin: processedData,
          burnRate: processedData,
          monthlyRevenue: processedData,
          monthlyExpenses: processedData,
          monthlySalaryDeductions: processedData
        });

        // # Calculate health metrics from last month's data
        if (processedData.length > 0) {
          const lastMonth = processedData[processedData.length - 1];
          const firstMonth = processedData[0];

          // # Calculate growth rate
          const revenueGrowth = firstMonth.revenue > 0
            ? ((lastMonth.revenue - firstMonth.revenue) / firstMonth.revenue) * 100
            : 0;

          // # Calculate average profit margin: (Net Revenue / Total Revenue) * 100
          const avgProfitMargin = processedData.reduce((sum, month) => {
            const monthlyProfitMargin = month.revenue > 0
              ? (month.netRevenue / month.revenue) * 100
              : 0;
            return sum + monthlyProfitMargin;
          }, 0) / processedData.length;

          // # Calculate average burn rate: (Monthly Expenses / Monthly Revenue) * 100
          const avgBurnRate = processedData.reduce((sum, month) => {
            const monthlyBurnRate = month.revenue > 0
              ? (month.expenses.total / month.revenue) * 100
              : 0;
            return sum + monthlyBurnRate;
          }, 0) / processedData.length;

          // # Calculate cash runway in months based on last month's data
          const cashRunway = lastMonth.revenue > 0
            ? lastMonth.availableCash / (lastMonth.expenses.total || 1)
            : 999;

          setHealthMetrics({
            averageProfitMargin: avgProfitMargin,
            averageBurnRate: avgBurnRate,
            cashRunway: cashRunway,
            revenueGrowth: revenueGrowth,
            cashFlowPositive: lastMonth.netRevenue > lastMonth.salaries,
            lastMonthRevenue: lastMonth.revenue,
            lastMonthExpenses: lastMonth.expenses.total,
            lastMonthProfit: lastMonth.netRevenue,
            expenseBreakdown: lastMonth.expenses.byCategory
          });
        }

      } catch (error) {
        console.error('Error fetching analytics data:', error);
        setError('Failed to load analytics data');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, []);

  return (
    <div className="fncl_hlth_container">
      {/* Financial Health Stats */}
      <div className="fncl_hlth_stats-grid">
        <div className="fncl_hlth_stat-card">
          <FaPercentage className="fncl_hlth_stat-icon" />
          <h3>
            Profit Margin
            <SignificanceMsg metricType="profitMargin" />
          </h3>
          <p className="fncl_hlth_stat-value">
            {healthMetrics.averageProfitMargin.toFixed(2)}%
          </p>
          {getTrendIndicator(healthMetrics.averageProfitMargin)}
        </div>
        <div className="fncl_hlth_stat-card">
          <FaFireAlt className="fncl_hlth_stat-icon" />
          <h3>
            Burn Rate
            <SignificanceMsg metricType="burnRate" />
          </h3>
          <p className="fncl_hlth_stat-value">
            {/* # CORRECTED: Display burn rate as percentage */}
            {healthMetrics.averageBurnRate.toFixed(2)}%
          </p>
        </div>
        <div className="fncl_hlth_stat-card">
          <FaWallet className="fncl_hlth_stat-icon" />
          <h3>
            Cash Runway
            <SignificanceMsg metricType="cashRunway" />
          </h3>
          <p className="fncl_hlth_stat-value">
            {healthMetrics.cashRunway.toFixed(1)} months
          </p>
        </div>
        <div className="fncl_hlth_stat-card">
          <FaChartLine className="fncl_hlth_stat-icon" />
          <h3>
            Revenue Growth
            <SignificanceMsg metricType="revenueGrowth" />
          </h3>
          <p className="fncl_hlth_stat-value">
            {healthMetrics.revenueGrowth.toFixed(2)}%
          </p>
          {getTrendIndicator(healthMetrics.revenueGrowth)}
        </div>
      </div>
      
      {/* Time Period Controls */}
      <div className="fncl_hlth_period-controls">
        <button 
          className={`fncl_hlth_period_button ${timePeriod === 'all' ? 'active' : ''}`}
          onClick={() => handlePeriodChange('all')}
        >
          All Time
        </button>
        <button 
          className={`fncl_hlth_period_button ${timePeriod === '3months' ? 'active' : ''}`}
          onClick={() => handlePeriodChange('3months')}
        >
          Last 3 Months
        </button>
        <button 
          className={`fncl_hlth_period_button ${timePeriod === '6months' ? 'active' : ''}`}
          onClick={() => handlePeriodChange('6months')}
        >
          Last 6 Months
        </button>
        <button 
          className={`fncl_hlth_period_button ${timePeriod === '1year' ? 'active' : ''}`}
          onClick={() => handlePeriodChange('1year')}
        >
          Last Year
        </button>
      </div>
      
      {/* Cash Flow Analysis */}
      <div className="fncl_hlth_cash_flow">
        <h3>Cash Flow Analysis</h3>
        <div className="fncl_hlth_cash_flow_grid">
          <div className="fncl_hlth_cash_flow_item">
            <h4>
              Last Month Revenue
              <SignificanceMsg metricType="netRevenue" />
            </h4>
            <p className="fncl_hlth_cash_flow_value">{formatCurrency(healthMetrics.lastMonthRevenue)}</p>
          </div>
          <div className="fncl_hlth_cash_flow_item">
            <h4>
              Last Month Expenses
              <SignificanceMsg metricType="budgetBalance" />
            </h4>
            <p className="fncl_hlth_cash_flow_value">{formatCurrency(healthMetrics.lastMonthExpenses)}</p>
          </div>
          <div className="fncl_hlth_cash_flow_item">
            <h4>
              Last Month Net Revenue
              <SignificanceMsg metricType="availableCash" />
            </h4>
            <p className="fncl_hlth_cash_flow_value">{formatCurrency(healthMetrics.lastMonthProfit)}</p>
            {getTrendIndicator(healthMetrics.lastMonthProfit > 0 ? 100 : -100)}
          </div>
        </div>
      </div>
      
      {/* Net Profit Margin Chart */}
      <div className="fncl_hlth_chart_container">
        <h3>Net Profit Margin</h3>
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart data={filteredData.netProfitMargin}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis yAxisId="left" orientation="left" />
            <YAxis yAxisId="right" orientation="right" />
            <Tooltip formatter={(value, name) => {
              // Check if the name contains "Margin" or "%" to format as percentage
              if (name.includes('Margin') || name.includes('%')) {
                return `${value.toFixed(2)}%`;
              }
              // Otherwise format as currency
              return formatCurrency(value);
            }} />
            <Legend />
            <Bar 
              yAxisId="left" 
              dataKey="revenue" 
              fill="#8884d8" 
              name="Gross Revenue" 
            />
            <Bar 
              yAxisId="left" 
              dataKey="salaries" 
              fill="#ff8042" 
              name="Salary Deductions" 
              stackId="expenses"
            />
            <Bar 
              yAxisId="left" 
              dataKey="budgetAllocation" 
              fill="#82ca9d" 
              name="Budget Allocation" 
              stackId="expenses"
            />
            <Bar 
              yAxisId="left" 
              dataKey="availableCash" 
              fill="#00a86b" 
              name="Available Cash" 
            />
            <Bar 
              yAxisId="left" 
              dataKey="expenses.total" 
              fill="#ff4242" 
              name="Total Expenses" 
            />
            <Line 
              yAxisId="right" 
              type="monotone" 
              dataKey="margin" 
              stroke="#82ca9d" 
              name="Profit Margin (%)" 
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      
      {/* Burn Rate Chart */}
      <div className="fncl_hlth_chart_container">
        <h3>Monthly Burn Rate Analysis</h3>
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart data={filteredData.burnRate}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
            <Tooltip formatter={(value, name) => {
              if (name.includes('Rate') || name.includes('%')) {
                return `${value.toFixed(2)}%`;
              }
              return formatCurrency(value);
            }} />
            <Legend />
            <Bar 
              yAxisId="left"
              dataKey="expenses.total" 
              fill="#ff8042" 
              name="Total Expenses" 
            />
            <Bar 
              yAxisId="left"
              dataKey="revenue" 
              fill="#82ca9d" 
              name="Total Revenue" 
            />
            <Line 
              yAxisId="right"
              type="monotone" 
              dataKey="burnRatePercentage" 
              stroke="#ff4242" 
              name="Burn Rate (% of Revenue)" 
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="fncl_hlth_chart_explanation">
          <p>
            <strong>Burn Rate:</strong> Shows the percentage of revenue being spent on expenses.
            Calculated as: (Total Expenses / Total Revenue) × 100
          </p>
          <ul>
            <li>Burn Rate: Percentage of total revenue being spent on expenses</li>
          </ul>
        </div>
      </div>
      
      {/* Revenue vs Expenses Chart */}
      <div className="fncl_hlth_chart_container">
        <h3>Revenue vs Expenses</h3>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="month" 
              allowDuplicatedCategory={false} 
              type="category"
            />
            <YAxis />
            <Tooltip formatter={(value) => formatCurrency(value)} />
            <Legend />
            <Line 
              data={filteredData.monthlyRevenue} 
              type="monotone" 
              dataKey="revenue" 
              stroke="#82ca9d" 
              name="Gross Revenue" 
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line 
              data={filteredData.monthlyRevenue} 
              type="monotone" 
              dataKey="netRevenue" 
              stroke="#8884d8" 
              name="Net Revenue" 
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line 
              data={filteredData.monthlyExpenses} 
              type="monotone" 
              dataKey="expenses.total" 
              stroke="#ff8042" 
              name="Budget Expenses" 
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line 
              data={filteredData.monthlySalaryDeductions} 
              type="monotone" 
              dataKey="salaries" 
              stroke="#ff4242" 
              name="Salary Deductions" 
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line 
              data={filteredData.monthlyRevenue} 
              type="monotone" 
              dataKey="availableCash" 
              stroke="#00a86b" 
              name="Available Cash" 
              strokeWidth={3}
              dot={{ r: 5 }}
              activeDot={{ r: 7 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default FinancialHealth; 